import React from 'react'
import PropTypes from 'prop-types'

import Container from '@hixme-ui/container'
import Select from '@hixme-ui/select'
import Text from '@hixme-ui/text'
import Title from '@hixme-ui/title'
import theme from '@hixme-ui/theme'
import { Button, Label } from '@hixme-ui/forms'
import { Row, Col } from 'react-flexbox-grid'

import Datepicker from 'components/Datepicker'
import LoadingView from 'components/LoadingView'
import { AllowContainer } from 'containers'
import { AvailableTagsContainer } from 'modules/tags/containers'

import { NoteCardContainer } from '../containers'
import { NOTE_TYPE } from '../constants'
import { defaultFontFamily } from '@sureco/design-system'

const NotesList = ({
  addNote,
  clearTags,
  endDate,
  expandAll,
  filterValues,
  handleChange,
  handleViewMore,
  hasMoreNotes,
  isPostingNote,
  isProcessing,
  newNote,
  noteAuthors,
  NoteContainer,
  noteList,
  noteLoading,
  noteTitle,
  refreshTagList,
  startDate,
  toggleAddNote,
  toggleSort,
}) => (
  <Container noPadding>
    <Title center>{noteTitle}</Title>
    <Container marginTop={addNote ? '50px' : '0'} textCenter={!addNote}>
      {noteLoading && (
        <Text primary style={{ marginRight: '10px' }}>
          Updating archives...
        </Text>
      )}
      {addNote ? (
        <NoteContainer
          note={newNote}
          isProcessing={isProcessing}
          isPostingNote={isPostingNote}
          onPostedNote={refreshTagList}
          onCancelPostNote={clearTags}
        />
      ) : (
        <Button onClick={toggleAddNote}>Add note</Button>
      )}
    </Container>
    <div
      style={{
        width: '100%',
        background: '#eff3f5',
        padding: '20px',
      }}
    >
      {!isProcessing && (
        <div>
          <AllowContainer platformHixmeAdmin platformBroker>
            <Row middle="xs" style={{ margin: '0 20px' }}>
              <Col xs={1}>
                <Label>Author</Label>
              </Col>
              <Col xs={6}>
                <Select
                  list={noteAuthors}
                  style={{
                    width: '100%',
                  }}
                  onChange={toggleSort}
                />
              </Col>
            </Row>
          </AllowContainer>
          <Row middle="xs" style={{ margin: '0 20px' }}>
            <Col xs={1}>
              <Label>From</Label>
            </Col>
            <Col xs={5}>
              <Datepicker
                selected={startDate}
                onChange={handleChange('startDate')}
                todayButton="Today"
              />
            </Col>
            <Col xs={1}>
              <Label>To</Label>
            </Col>
            <Col xs={5}>
              <Datepicker
                selected={endDate}
                onChange={handleChange('endDate')}
                todayButton="Today"
              />
            </Col>
          </Row>
          <AvailableTagsContainer itemType={NOTE_TYPE} />
          <Row style={{ margin: '10px 20px' }}>
            <Col xs={6}>
              <Text>
                Sort by{' '}
                <span
                  role="button"
                  tabIndex={-1}
                  onClick={toggleSort('CreatedDateDesc')}
                  style={{
                    fontFamily: defaultFontFamily,
                    color: !filterValues.asc
                      ? theme.colors.primary
                      : theme.colors.blue,
                    cursor: 'pointer',
                  }}
                >
                  Newest
                </span>{' '}
                |{' '}
                <span
                  role="button"
                  tabIndex={-1}
                  onClick={toggleSort('CreatedDateAsc')}
                  style={{
                    fontFamily: defaultFontFamily,
                    color: filterValues.asc
                      ? theme.colors.primary
                      : theme.colors.blue,
                    cursor: 'pointer',
                  }}
                >
                  Oldest
                </span>
              </Text>
            </Col>
            <Col xs={6} style={{ textAlign: 'right' }}>
              <Text a blue tabIndex={-1} onClick={expandAll(true)}>
                Expand all
              </Text>{' '}
              |{' '}
              <Text a blue tabIndex={-1} onClick={expandAll(false)}>
                Collapse all
              </Text>
            </Col>
          </Row>
        </div>
      )}
      {isProcessing && <LoadingView />}
      {!isProcessing &&
        noteList.map((note) => <NoteCardContainer key={note.Id} note={note} />)}
      {!isProcessing && hasMoreNotes && (
        <Container style={{ width: '100%' }} padding="0 0 20px 0" textCenter>
          <Button outline style={{ width: '50%' }} onClick={handleViewMore}>
            View more
          </Button>
        </Container>
      )}
    </div>
  </Container>
)

NotesList.displayName = 'NotesList'

NotesList.propTypes = {
  addNote: PropTypes.bool,
  clearTags: PropTypes.func.isRequired,
  endDate: PropTypes.object,
  expandAll: PropTypes.func.isRequired,
  filterValues: PropTypes.object,
  handleChange: PropTypes.func.isRequired,
  handleViewMore: PropTypes.func.isRequired,
  hasMoreNotes: PropTypes.bool,
  isPostingNote: PropTypes.bool,
  isProcessing: PropTypes.bool,
  newNote: PropTypes.object,
  noteAuthors: PropTypes.array,
  NoteContainer: PropTypes.func.isRequired,
  noteList: PropTypes.array.isRequired,
  noteLoading: PropTypes.string,
  noteTitle: PropTypes.string,
  refreshTagList: PropTypes.func.isRequired,
  startDate: PropTypes.object,
  toggleAddNote: PropTypes.func.isRequired,
  toggleSort: PropTypes.func.isRequired,
}

NotesList.defaultProps = {
  addNote: false,
  endDate: null,
  filterValues: {},
  hasMoreNotes: false,
  isPostingNote: false,
  isProcessing: false,
  newNote: {},
  noteAuthors: [],
  noteLoading: '',
  noteTitle: 'Notes',
  startDate: null,
}

export default NotesList
